import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { isModalOpen, setToastMessage } from '../redux/FlowReducer';
import { Box, Typography, Select, MenuItem, FormControl, InputLabel, Card, CardContent } from '@mui/material';
import VariableAutocomplete from '../VariableAutocomplete';
import PanelShell from './PanelShell';

const LoopPanel = ({ nodeType, nodeId, data, onChange }) => {
  const dispatch = useDispatch();
  const vars = useSelector((state) => state.flow.vars) || [];
  const tags = useSelector((state) => state.flow.tags) || [];
  const localVars = useSelector((state) => state.flow.localVars) || [];

  const [focusedInput, setFocusedInput] = useState(null);
  const [parameterMappings, setParameterMappings] = useState({});
  const [selectedCondition, setSelectedCondition] = useState(data?.selectedCondition || '');

  // Parse variable string to get proper variable object
  const parseVariableString = (value, inputType) => {
    if (!value) return null;

    if (inputType === 'text') {
      return {
        variableId: value,
        variableName: value,
        variableType: 'constant'
      };
    }

    // Check for different variable types
    if (value.startsWith('lv_')) {
      const varName = value.substring(3);
      const foundLocalVar = localVars.find(v => v.name === varName);
      return {
        variableId: value,
        variableName: foundLocalVar ? foundLocalVar.name : value,
        variableType: 'LV'
      };
    }

    if (value.startsWith('var_')) {
      const varId = value.substring(4);
      const foundVar = vars.find(v => {
        const varIdValue = v.varId !== undefined ? v.varId : v.varID;
        return String(varIdValue) === String(varId);
      });

      return {
        variableId: value,
        variableName: foundVar ? foundVar.varName : value,
        variableType: 'V'
      };
    }

    if (value.startsWith('tag_')) {
      const tagId = value.substring(4);
      const foundTag = tags.find(t => {
        const tagIdValue = t.tagId !== undefined ? t.tagId : t.tagID;
        return String(tagIdValue) === String(tagId);
      });

      return {
        variableId: value,
        variableName: foundTag ? foundTag.tagName : value,
        variableType: 'T'
      };
    }

    return {
      variableId: value,
      variableName: value,
      variableType: 'constant'
    };
  };

  // Initialize data when component mounts or when data prop changes
  useEffect(() => {
    if (!data) return;

    setSelectedCondition(data.selectedCondition || '');

    const newMappings = {
      input1: parseVariableString(data.selectedVariable1, data.inputType1),
      input2: parseVariableString(data.selectedVariable2, data.inputType2)
    };

    console.log('Initialized Loop Mappings:', newMappings);
    setParameterMappings(newMappings);
  }, [data, vars, tags, localVars]);

  const getColorForType = (type) => {
    switch(type) {
      case 'V': return '#fff3e0';
      case 'T': return '#edbc45';
      case 'LV': return '#f3e5f5';
      case 'constant': return '#e0e0e0';
      default: return '#f5f5f5';
    }
  };

  const handleVariableSelect = (event, variable) => {
    if (!focusedInput || !variable) return;

    console.log('Selected Variable:', variable);

    let variableId;
    let variableName;
    const variableType = variable.type;

    switch (variableType) {
      case 'LV':
        variableId = `lv_${variable.name}`;
        variableName = variable.name;
        break;
      case 'V':
        const varIdValue = variable.varID || variable.varId || variable.id;
        variableId = `var_${varIdValue}`;
        variableName = variable.varName || variable.name;
        break;
      case 'T':
        const tagIdValue = variable.tagID || variable.tagId || variable.id;
        variableId = `tag_${tagIdValue}`;
        variableName = variable.tagName || variable.name;
        break;
      case 'constant':
        variableId = variable.name;
        variableName = variable.name;
        break;
      default:
        variableId = variable.id;
        variableName = variable.name;
    }

    setParameterMappings(prev => ({
      ...prev,
      [focusedInput]: {
        variableId,
        variableName,
        variableType
      }
    }));
  };

  const InputCard = ({ label, inputKey, mapping }) => {
    const isFocused = focusedInput === inputKey;

    return (
      <Card
        onClick={() => setFocusedInput(inputKey)}
        sx={{
          cursor: 'pointer',
          border: isFocused ? '2px solid #6a1b9a' : '1px solid #ddd',
          backgroundColor: isFocused ? '#f3e5f5' : 'white',
          mb: 1
        }}
      >
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="body2" fontWeight={600} mb={0.5} color="text.secondary">
            {label}
          </Typography>

          {mapping?.variableName ? (
            <Box sx={{
              p: 1,
              backgroundColor: getColorForType(mapping.variableType),
              borderRadius: 1,
              fontSize: '0.9rem'
            }}>
              {mapping.variableName}
            </Box>
          ) : (
            <Typography variant="body2" color="text.disabled" fontStyle="italic">
              Değişken seçilmedi
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  };

  const conditionOptions = [
    { value: "=", label: "=" },
    { value: "!=", label: "!=" },
    { value: ">", label: ">" },
    { value: "<", label: "<" },
    { value: ">=", label: "≥" },
    { value: "<=", label: "≤" }
  ];

  const handleSave = () => {
    const { input1, input2 } = parameterMappings;

    if (!input1 || !input2 || !selectedCondition) {
      dispatch(setToastMessage({
        type: 'error',
        message: 'Lütfen tüm alanları doldurun.'
      }));
      return;
    }

    const getVariableString = (mapping) => {
      if (!mapping) return '';
      return mapping.variableId;
    };

    const title = `Loop until: ${input1.variableName} ${selectedCondition} ${input2.variableName}`;

    const nodeData = {
      selectedCondition,
      selectedVariable1: getVariableString(input1),
      selectedVariable2: getVariableString(input2),
      inputType1: input1.variableType === 'constant' ? 'text' : 'select',
      inputType2: input2.variableType === 'constant' ? 'text' : 'select',
      label: 'Loop',
      Title: title,
    };

    console.log('Saving Loop Node Data:', nodeData);

    onChange(nodeData);
    dispatch(isModalOpen());
    dispatch(setToastMessage({
      type: 'success',
      message: 'Döngü tanımı kaydedildi.'
    }));
  };

  if (nodeType !== "Loop") return null;

  return (
    <PanelShell title="Döngü İşlemi" onSave={handleSave}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: '100%', overflow: 'hidden' }}>
        {/* Variable Selector */}
        <Box>
          <Typography variant="body2" fontWeight={600} mb={1} color="text.secondary">
            Değişken / Sabit Seçimi
          </Typography>
          <VariableAutocomplete
            value={null}
            onChange={handleVariableSelect}
            placeholder="Tag, değişken veya sabit ara..."
            showConstants={true}
          />
          {focusedInput && (
            <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 1 }}>
              Seçilen alan: {focusedInput === 'input1' ? 'Değişken 1' : 'Değişken 2'}
            </Typography>
          )}
        </Box>

        {/* Input Parameters */}
        <Box>
          <InputCard
            label="Değişken 1"
            inputKey="input1"
            mapping={parameterMappings.input1}
          />

          <FormControl fullWidth size="small" sx={{ mb: 1 }}>
            <InputLabel>Koşul</InputLabel>
            <Select
              value={selectedCondition}
              label="Koşul"
              onChange={(e) => setSelectedCondition(e.target.value)}
            >
              {conditionOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <InputCard
            label="Değişken 2"
            inputKey="input2"
            mapping={parameterMappings.input2}
          />
        </Box>
      </Box>
    </PanelShell>
  );
};

export default LoopPanel;
